import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Image, Send, AlertCircle, CheckCircle, Upload } from 'lucide-react';
import Button from '../UI/Button';
import apiClient from '../../services/api';

const ACCEPT_TYPES = 'image/jpeg,image/jpg,image/png,image/webp';
const MAX_SIZE_MB = 5;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

export interface ImageUploadResult {
  imageUrl: string;
  filePath: string;
}

interface ImageMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (imageUrl: string, imageFilePath: string, imageMessageBody?: string) => Promise<void>;
  onShowCostConfirm?: (imageUrl: string, imageFilePath: string, imageMessageBody?: string) => void;
  selectedCount: number;
  theme: 'dark' | 'light';
  loading?: boolean;
}

export const ImageMessageModal: React.FC<ImageMessageModalProps> = ({
  isOpen,
  onClose,
  onSend,
  onShowCostConfirm,
  selectedCount,
  theme,
  loading = false
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageMessageBody, setImageMessageBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const clearFile = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setSelectedFile(null);
    setImageMessageBody('');
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateFile = (file: File): string | null => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return 'Invalid file type. Only JPG, JPEG, PNG and WebP are allowed.';
    }
    if (file.size > MAX_SIZE_BYTES) {
      return `File too large. Maximum size is ${MAX_SIZE_MB} MB.`;
    }
    return null;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    const err = validateFile(file);
    if (err) {
      setError(err);
      setSelectedFile(null);
      setPreviewUrl(null);
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const uploadImage = async (): Promise<ImageUploadResult> => {
    if (!selectedFile) throw new Error('No file selected');
    const formData = new FormData();
    formData.append('image', selectedFile);
    const { data } = await apiClient.post<{ success: boolean; image_url?: string; file_path?: string; error?: string }>(
      '/upload/whatsapp-image/',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    if (!data.success || !data.image_url || !data.file_path) {
      throw new Error(data.error || 'Upload failed');
    }
    return { imageUrl: data.image_url, filePath: data.file_path };
  };

  const handleSend = async () => {
    if (!selectedFile) {
      setError('Please select an image file');
      return;
    }

    setIsUploading(true);
    setError(null);
    try {
      const { imageUrl, filePath } = await uploadImage();
      const caption = imageMessageBody.trim();
      if (onShowCostConfirm) {
        clearFile();
        onClose();
        setTimeout(() => onShowCostConfirm(imageUrl, filePath, caption || undefined), 150);
      } else {
        await onSend(imageUrl, filePath, caption || undefined);
        clearFile();
      }
    } catch (err: any) {
      const message = err.response?.data?.error || err.message || 'Failed to upload image. Please try again.';
      setError(message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    clearFile();
    onClose();
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) handleClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      const origOverflow = document.body.style.overflow || '';
      const origPosition = document.body.style.position || '';
      const origWidth = document.body.style.width || '';
      const origTop = document.body.style.top || '';
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollY}px`;
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = origOverflow;
        document.body.style.position = origPosition;
        document.body.style.width = origWidth;
        document.body.style.top = origTop;
        window.scrollTo(0, scrollY);
      };
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const handleModalScroll = (e: React.WheelEvent) => e.stopPropagation();
  const handleModalTouchMove = (e: React.TouchEvent) => e.stopPropagation();

  if (!isOpen) return null;

  const canSend = !!selectedFile && !error;
  const busy = loading || isUploading;

  const modalContent = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4"
      onWheel={handleModalScroll}
      onTouchMove={handleModalTouchMove}
    >
      <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleOverlayClick} />
      <div
        className="relative w-full max-w-md mx-2 sm:mx-0 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto overscroll-contain"
        onWheel={handleModalScroll}
        onTouchMove={handleModalTouchMove}
      >
        <div className={`w-full rounded-xl shadow-2xl ${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <div className={`flex items-center justify-between p-4 sm:p-6 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
              <div className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 ${theme === 'dark' ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
                <Image className={`h-4 w-4 sm:h-5 sm:w-5 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className={`text-base sm:text-lg font-semibold truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Send Image Message
                </h3>
                <p className={`text-xs sm:text-sm truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Send an image to {selectedCount} selected guest{selectedCount > 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className={`p-1.5 sm:p-2 rounded-lg transition-colors flex-shrink-0 ml-2 ${theme === 'dark' ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
            >
              <X className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </div>

          <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
            <div>
              <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                Choose image
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT_TYPES}
                onChange={handleFileChange}
                className="hidden"
              />
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={busy}
                  className="flex items-center justify-center gap-2"
                >
                  <Upload className="h-4 w-4" />
                  {selectedFile ? 'Change image' : 'Select image'}
                </Button>
                {selectedFile && (
                  <span className={`text-xs sm:text-sm self-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </span>
                )}
              </div>
              {error && (
                <div className="flex items-center gap-1.5 sm:gap-2 mt-1.5 sm:mt-2 text-red-600">
                  <AlertCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="text-xs sm:text-sm">{error}</span>
                </div>
              )}
            </div>

            <div>
              <label
                htmlFor="image-message-body"
                className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}
              >
                WhatsApp message (optional)
              </label>
              <textarea
                id="image-message-body"
                value={imageMessageBody}
                onChange={(e) => setImageMessageBody(e.target.value)}
                disabled={busy}
                rows={3}
                maxLength={1024}
                placeholder="Shown above the image for guests (marketing template). Leave empty to use the default line."
                className={`w-full rounded-lg border px-3 py-2 text-sm resize-y min-h-[4.5rem] ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
              />
            </div>

            {previewUrl && (
              <div className="space-y-1.5 sm:space-y-2">
                <label className={`block text-xs sm:text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Preview
                </label>
                <div className={`border rounded-lg p-2 sm:p-4 ${theme === 'dark' ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'}`}>
                  <img src={previewUrl} alt="Preview" className="w-full h-24 sm:h-32 object-contain rounded-lg" />
                </div>
                <div className="flex items-center gap-1.5 text-green-600">
                  <CheckCircle className="h-4 w-4 flex-shrink-0" />
                  <span className="text-xs sm:text-sm">JPG, PNG or WebP, max 5 MB</span>
                </div>
              </div>
            )}

            <div className={`text-[10px] sm:text-xs leading-relaxed ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              <p>• Select an image (JPG, JPEG, PNG or WebP, max 5 MB)</p>
              <p>
                • Optional text is sent as the template body where your Twilio template has a body variable (e.g.{' '}
                {'{{1}}'})
              </p>
              <p>• The image will be sent to all selected guests via WhatsApp</p>
            </div>
          </div>

          <div className={`flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3 p-4 sm:p-6 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
            <Button variant="secondary" onClick={handleClose} disabled={busy} className="w-full sm:w-auto order-2 sm:order-1">
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSend}
              disabled={busy || !canSend}
              className="flex items-center justify-center gap-2 w-full sm:w-auto order-1 sm:order-2"
            >
              {busy ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  <span className="text-sm sm:text-base">{isUploading ? 'Uploading...' : 'Sending...'}</span>
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  <span className="text-sm sm:text-base">Send Image</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
